import { SPTracker } from './sp-tracker';
import Categories from '../../dictionary/tracking/categories';
import Actions from '../../dictionary/tracking/actions';
import globalStore from '@/store/global';
import { PortalType, Referrer } from '../../../interfaces';
import { Action, Product, Tracker } from '@studyportals/datalake-event-tracker';
import { PopUpType } from '@/domain/enums/pop-up-type';
import { OptInEvent } from './enums/optin-event.enum';
import { optInVariations } from './constants/optin-variation.constant';
import { rollbar as rollbarInstance } from '../rollbar/rollbar';
import { AuthenticationProviderType } from '@/domain/enums/authentication-provider-type';

declare const STAGE: string;

export default class Tracking {
	private spTracker: SPTracker;
	private datalakeTrackerSnowAndGA: Tracker;

	constructor() {
		this.spTracker = new SPTracker({
			environment: STAGE,
			team: 'xm',
			product: 'AuthService',
			category: Categories.PROFILE,
		});

		this.datalakeTrackerSnowAndGA = new Tracker(Product.PROFILE_QUESTIONNAIRES, true, true);
	}

	public trackOptInEvent(optInEvent: OptInEvent, version: PopUpType): void {
		if (optInEvent === OptInEvent.IMPRESSION) {
			this.datalakeTrackerSnowAndGA.trackExperimentEvent({
				category: Product.EXPERIMENT,
				action: Action.IMPRESSION,
				label: optInVariations[version]!,
			})

			return;
		}

		this.datalakeTrackerSnowAndGA.trackExperimentEvent({
			category: Product.EXPERIMENT,
			action: Action.CLICK,
			label: optInEvent,
			property: optInVariations[version],
		})
	}

	private ignoreErrors(errorType: string):boolean {
		const ignoreErrorTypes = [
			"UsernameOrPasswordIncorrectError",
			"UsernameExistsError",
			"InvalidCaptchaTokenError",
			"LinkedInPopupClosedError",
		];
		return ignoreErrorTypes.includes(errorType);
	}

	public track(event, snowPlow = true, spAnalytics = false): void {
		if (snowPlow) {
			this.snowplow(event);
		}

		if (spAnalytics) {
			this.spAnalytics(event);
		}
	}

	public trackError(error, referrer: Referrer | null): void {
		const errorType = (error)? error?.name: 'UnknownError';
		const eventData = error instanceof Error ? error.stack || '' : JSON.stringify(error);

		if(!this.ignoreErrors(errorType as string)) {
			this.rollbar(error);
		}

		const tags: (Referrer | AuthenticationProviderType | PortalType)[] = [];
		if (globalStore.getters.authProvider()) {
			tags.push(globalStore.getters.authProvider()!);
		}

		if (globalStore.getters.portalType()) {
			tags.push(globalStore.getters.portalType()!);
		}

		if (referrer) {
			tags.push(referrer);
		}

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.spTracker.track({
			action: Actions.GENERAL,
			label: errorType,
			tags,
			eventData,
		});
	}

	private rollbar(error): void | null {

		if(!(error instanceof Error)) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
			error = new Error(error);
		}

		if (!rollbarInstance) {
			return null;
		}

		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		rollbarInstance.error(error as Error);

	}

	private snowplow(event): void | null {
		if (typeof window['snowplow'] === 'undefined') {
			return null;
		}
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
		const trackingEvent = event.get('redshift');

		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		window['snowplow']('trackStructEvent', {
			category: trackingEvent.category,
			action: trackingEvent.action,
			label: trackingEvent.label || '',
			property: trackingEvent.property || '',
			value: trackingEvent.value || 0,
		});
	}

	private spAnalytics(event): void {
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.spTracker.track(
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call
			event.get('spanalytics'),
		);
	}
}
